import React, { useEffect, useRef, useState } from 'react'
import useTranslation, {
  TranslationProvider,
} from '~/components/shared/TranslationProvider'
import * as translations from './locales.json'
import { MessagingProvider, useMessaging } from './MessagingProvider'
import { BrowserRouter } from 'react-router'
import { Button } from '~/lib/ui/button'
import Layout from './Layout'
import { TabNav, ButtonContainer, TabButton } from '~/components/shared/TabNav'
import { Toaster } from '~/lib/ui/toast'
import { Form } from '~/lib/ui/form'
import { InputField } from '~/components/shared/FormFields'

const BronzeText = () => {
  const { t } = useTranslation('index')
  const { platform_name } = useMessaging()

  return <>{t('view_and_create_messages_bronze', { platform_name })}</>
}
const SilverText = () => {
  const { t } = useTranslation('index')
  const { platform_name } = useMessaging()

  return <>{t('view_and_create_messages_silver', { platform_name })}</>
}
const GoldText = () => {
  const { t } = useTranslation('index')
  const { platform_name } = useMessaging()

  return <>{t('view_and_create_messages_gold', { platform_name })}</>
}
const CoachText = () => {
  const { t } = useTranslation('index')
  const { platform_name } = useMessaging()

  return (
    <>
      <p className="mb-4">
        {t('messaging_instructions_for_coaching_staff', { platform_name })}
      </p>
      <p>{t('messaging_instructions_for_coaching_staff_cont')}</p>
    </>
  )
}

const MainView = () => {
  const {
    isMobile,
    currentThreadId,
    newMessageMode,
    setNewMessageMode,
    goBackToList,
    userIsCoach,
    programIsBronze,
    programIsSilver,
    programIsGold,
    form,
  } = useMessaging()

  const headerRef = useRef(null)
  const [layoutHeight, setLayoutHeight] = useState(450)

  useEffect(() => {
    const el = headerRef.current
    if (!el) return
    const update = () => {
      const bottom = el.getBoundingClientRect().bottom
      setLayoutHeight(Math.max(window.innerHeight - Math.ceil(bottom), 450))
    }
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const handleNewMessage = () => {
    setNewMessageMode(true)
  }

  const isThreadView = !currentThreadId && !newMessageMode

  const showBackButton = isMobile && !isThreadView

  const onChangeActiveTab = (activeTab) => {
    if (activeTab === 'reminders') {
      window.location.href = userIsCoach ? '/coach/alerts' : '/reminders'
    }
  }

  const { t } = useTranslation('index')

  return (
    <>
      <div ref={headerRef}>
        <div className="mx-4 mt-4 flex flex-col justify-between md:mx-9">
          <div className="flex flex-col justify-between gap-4 md:flex-row">
            <h1 className="mt-4 !text-intoo-green-coachcta">
              {userIsCoach ? (
                <>{t('messages')}</>
              ) : (
                <>
                  <span className="pr-2 font-normal">{t('view_your')}</span>
                  {t('messages')}
                </>
              )}
            </h1>
          </div>
          <div className="flex flex-col justify-between xl:flex-row xl:gap-x-16">
            <div className="xl:flex-basis-2/3 xl:flex-grow">
              {userIsCoach && <CoachText />}
              {programIsBronze && <BronzeText />}
              {programIsSilver && <SilverText />}
              {programIsGold && <GoldText />}
            </div>
            <div>
              <Button
                className="mt-4 w-full hover:text-white focus:text-white"
                variant="coachcta"
                onClick={handleNewMessage}
              >
                {t('create_message')}
              </Button>
            </div>
          </div>
          <TabNav
            defaultActiveTab="messages"
            mobileMode="swipe"
            className="my-4 md:flex md:flex-row md:justify-between md:border-b md:border-solid md:border-[#979797]"
            onChangeActiveTab={onChangeActiveTab}
          >
            <ButtonContainer className="justify-start md:border-none">
              <TabButton
                name="messages"
                text={t('messages_tab_heading')}
                activeDecorationClass="bg-intoo-green-coachcta"
              />
              <TabButton
                name="reminders"
                text={
                  userIsCoach ? 'Notifications' : t('reminders_tab_heading')
                }
                activeDecorationClass="bg-intoo-green-coachcta"
              />
            </ButtonContainer>
            {false && !isMobile && (
              <Button
                className="w-auto hover:text-white focus:text-white"
                variant="coachcta"
                onClick={handleNewMessage}
              >
                {t('create_message')}
              </Button>
            )}
          </TabNav>
          {showBackButton && (
            <div>
              <Button
                variant="outline"
                className="mb-4 w-full text-cod-gray-700 hover:text-cod-gray-700"
                onClick={goBackToList}
              >
                {t('back_to_all_messages')}
              </Button>
            </div>
          )}
        </div>
        {userIsCoach && (
          <Form {...form}>
            <form className="flex space-x-4 md:ml-8">
              <InputField
                name="candidateSearch"
                control={form.control}
                className="md:w-1/3"
                label="Search Candidate:"
              />
            </form>
          </Form>
        )}
      </div>
      <Layout layoutHeight={layoutHeight} />
      <Toaster />
    </>
  )
}

const CoachMessaging = ({ locale, ...props }) => {
  return (
    <BrowserRouter>
      <TranslationProvider
        locale={locale}
        translations={translations}
        scope={'javascript.components.CoachMessaging'}
      >
        <MessagingProvider {...props} locale={locale}>
          <MainView />
        </MessagingProvider>
      </TranslationProvider>
    </BrowserRouter>
  )
}

export default CoachMessaging
